import { useState, useCallback } from 'react';

/**
 * 可重複使用的拖拽排序 Hook
 * @param {Object} options - 配置選項
 * @param {Array} options.items - 要排序的項目列表
 * @param {Function} options.onReorder - 重新排序後的回調函數 (items) => Promise
 * @param {string} options.sortBy - 排序依據的欄位名稱，默認 'order'
 * @param {string} options.idField - 唯一識別欄位名稱，默認 'id'
 * @param {Object} options.apiConfig - API 配置
 * @returns {Object} 拖拽相關的狀態和處理函數
 */
export const useDragSort = ({
  items = [],
  onReorder,
  sortBy = 'order',
  idField = 'id',
  apiConfig = {}
}) => {
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // 獲取排序後的項目列表
  const getSortedItems = useCallback(() => {
    return [...items].sort((a, b) => (a[sortBy] || 0) - (b[sortBy] || 0));
  }, [items, sortBy]);

  // 拖拽開始
  const handleDragStart = useCallback((e, item, index) => {
    setDraggedItem({ ...item, index });
    e.dataTransfer.effectAllowed = 'move';
    // e.target.style.opacity = '0.5';  // 移除，改由 CSS class 控制
  }, []);

  // 拖拽結束
  const handleDragEnd = useCallback((e) => {
    // e.target.style.opacity = '1';  // 移除，改由 CSS class 控制
    setDraggedItem(null);
    setDragOverIndex(null);
  }, []);

  // 拖拽經過
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  // 拖拽進入
  const handleDragEnter = useCallback((e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  }, []);

  // 放置
  const handleDrop = useCallback(async (e, dropIndex) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.index === dropIndex) {
      setDraggedItem(null);
      setDragOverIndex(null);
      return;
    }

    // 使用排序後的項目列表進行重新排序
    const sortedItems = getSortedItems();
    const draggedElement = sortedItems.splice(draggedItem.index, 1)[0];
    sortedItems.splice(dropIndex, 0, draggedElement);

    // 更新每個項目的排序值
    const reorderedItems = sortedItems.map((item, index) => ({
      ...item,
      [sortBy]: index + 1
    }));

    // 調用重新排序回調
    if (onReorder) {
      try {
        await onReorder(reorderedItems);
      } catch (error) {
        console.error('Reorder failed:', error);
        throw error; // 讓調用者處理錯誤
      }
    }

    setDraggedItem(null);
    setDragOverIndex(null);
  }, [draggedItem, getSortedItems, onReorder, sortBy]);

  // 重新排序項目 (程序化調用)
  const reorderItems = useCallback(async (fromIndex, toIndex) => {
    const sortedItems = getSortedItems();
    const draggedElement = sortedItems.splice(fromIndex, 1)[0];
    sortedItems.splice(toIndex, 0, draggedElement);

    const reorderedItems = sortedItems.map((item, index) => ({
      ...item,
      [sortBy]: index + 1
    }));

    if (onReorder) {
      await onReorder(reorderedItems);
    }

    return reorderedItems;
  }, [getSortedItems, onReorder, sortBy]);

  // 拖拽狀態
  const dragState = {
    draggedItem,
    dragOverIndex,
    isDragging: Boolean(draggedItem),
    isDraggedOver: (index) => dragOverIndex === index,
    isDraggedItem: (index) => draggedItem && draggedItem.index === index
  };

  // 拖拽事件處理函數
  const dragHandlers = {
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragEnter,
    handleDrop
  };

  return {
    dragState,
    dragHandlers,
    reorderItems,
    getSortedItems
  };
};

export default useDragSort;